import { AlertTriangle, RefreshCw, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  endStreamSession,
  loadStartStreamState,
  releaseWelcomeQueue,
  setStreamStyle,
  setWelcomeFallback,
  startStreamSession,
  startWelcomeCycle,
  type EffectsSnapshot,
  type ProductionSnapshot,
  type StreamEffectStyle,
  type StartStreamSound,
  type TwitchBotState,
} from './stream-api';
import './workspace.css';

type State = {
  effects: EffectsSnapshot;
  production: ProductionSnapshot;
  bot: TwitchBotState;
  sounds: StartStreamSound[];
};

function status(ok: boolean, label: string, detail: string) {
  return { ok, label, detail };
}

export function StartStreamPage() {
  const [state, setState] = useState<State | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [clock, setClock] = useState(Date.now());

  const refresh = useCallback(async () => {
    try {
      setState(await loadStartStreamState());
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Stream setup could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 3000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const selectedStyle = useMemo(
    () => state?.effects.styles.find((item) => item.style === state.effects.selectedStyle) ?? null,
    [state],
  );
  const fallback = useMemo(
    () => state?.effects.welcomeFallbacks.find((item) => item.style === state.effects.selectedStyle) ?? null,
    [state],
  );
  const session = state?.effects.activeSession ?? null;
  const releaseSeconds = session?.welcomeReleaseDueAt
    ? Math.max(0, Math.ceil((new Date(session.welcomeReleaseDueAt).getTime() - clock) / 1000))
    : null;

  const checks = state
    ? [
        status(state.production.activeAgent?.connected === true, 'Stream PC', state.production.activeAgent?.connected ? `Connected · ${state.production.activeAgent.agentVersion ?? 'version unknown'}` : 'Agent is offline.'),
        status(state.production.obs.status === 'online', 'OBS', state.production.obs.status === 'online' ? state.production.obs.currentProgramSceneName || 'Connected' : state.production.obs.lastError || 'OBS is unavailable.'),
        status(state.production.soundboard.status === 'online' && state.production.soundboard.overlayClients > 0, 'Stream audio', state.production.soundboard.status === 'online' && state.production.soundboard.overlayClients > 0 ? `${state.production.soundboard.overlayClients} audio client${state.production.soundboard.overlayClients === 1 ? '' : 's'} connected` : state.production.soundboard.lastError || 'Audio browser source is not ready.'),
        status(state.bot.enabled && state.bot.connected && state.bot.scopesReady, 'Chat bot', state.bot.enabled && state.bot.connected && state.bot.scopesReady ? `${state.bot.botName ?? 'FFSControl'} connected` : state.bot.lastError || 'Bot connection or permissions need checking.'),
        status(Boolean(fallback?.configured && fallback.enabled), 'Welcome audio', fallback?.configured && fallback.enabled ? fallback.soundName || 'Fallback configured' : fallback?.configured ? 'Configured fallback sound is disabled.' : 'Fallback welcome sound is not configured.'),
      ]
    : [];
  const blockers = checks.filter((item) => !item.ok);

  async function act(operation: () => Promise<EffectsSnapshot>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      const effects = await operation();
      setState((current) => (current ? { ...current, effects } : current));
      setMessage(success);
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  }

  async function changeStyle(style: StreamEffectStyle) {
    await act(() => setStreamStyle(style), 'Stream theme updated.');
  }

  async function changeFallback(soundId: string | null) {
    if (!state) return;
    await act(
      () => setWelcomeFallback(state.effects.selectedStyle, soundId),
      soundId ? 'Fallback welcome audio updated.' : 'Fallback welcome audio cleared.',
    );
  }

  async function beginSession() {
    if (blockers.length > 0) {
      const list = blockers.map((item) => `• ${item.label}: ${item.detail}`).join('\n');
      if (!window.confirm(`Start the FFSControl stream session with ${blockers.length} readiness check${blockers.length === 1 ? '' : 's'} still unresolved?\n\n${list}\n\nThis does not change OBS scenes.`)) return;
    }
    await act(startStreamSession, blockers.length > 0 ? 'Stream session started with unresolved readiness checks.' : 'Stream session started.');
  }

  async function releaseNow() {
    if (!window.confirm('Release the welcome queue immediately?\n\nThis bypasses the OBS scene gate and the normal 45-second grace period.')) return;
    await act(releaseWelcomeQueue, 'Welcome queue released manually.');
  }

  async function newCycle() {
    if (!window.confirm('Start a new Welcome Cycle?\n\nViewers may be welcomed again. Viewer and chat history are kept.')) return;
    await act(startWelcomeCycle, 'New Welcome Cycle started.');
  }

  async function endSession() {
    if (!window.confirm('End the current stream session?\n\nAny queued welcomes will be skipped.')) return;
    await act(endStreamSession, 'Stream session ended.');
  }

  if (loading && !state) return <div className="ffs-next-loading">Loading stream setup…</div>;

  return (
    <div className="ffs-workspace-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}

      {state ? (
        <>
          <section className="ffs-workspace-hero">
            <div>
              <small>STREAM SETUP</small>
              <h2>{session ? 'Stream session active' : blockers.length ? `${blockers.length} item${blockers.length === 1 ? '' : 's'} to check` : 'Ready to start the session'}</h2>
              <p>{session ? `Welcome Cycle #${session.welcomeGeneration} · ${selectedStyle?.label ?? session.style}` : 'Choose the presentation and manage the welcome session. Live system status is in Control Room.'}</p>
            </div>
            <button type="button" className="ffs-button secondary" onClick={() => void refresh()} disabled={busy}>
              <RefreshCw size={15} /> Refresh
            </button>
          </section>

          <section className="ffs-workspace-card ffs-start-theme-card">
            <header>
              <div><small>1 · PRESENTATION</small><h3>Choose stream theme</h3></div>
              <span className="ffs-start-theme-current">CURRENT · {selectedStyle?.label ?? state.effects.selectedStyle}</span>
            </header>

            <div className="ffs-start-theme-controls">
              <label className="ffs-start-theme-picker">
                <span>Stream theme</span>
                <select
                  value={state.effects.selectedStyle}
                  disabled={busy || Boolean(session)}
                  onChange={(event) => void changeStyle(event.target.value as StreamEffectStyle)}
                >
                  {state.effects.styles.map((style) => (
                    <option value={style.style} key={style.style}>{style.label}</option>
                  ))}
                </select>
              </label>

              <div className="ffs-start-theme-route" aria-label="Selected theme OBS lifecycle">
                <div>
                  <small>STARTUP</small>
                  <strong>{selectedStyle?.startupSceneName || 'Not configured'}</strong>
                </div>
                <span>→</span>
                <div>
                  <small>MAIN</small>
                  <strong>{selectedStyle?.mainSceneName || 'Not configured'}</strong>
                </div>
                <div className="ffs-start-theme-gate">
                  <small>WELCOME GATE</small>
                  <strong>{selectedStyle ? `${Math.round(selectedStyle.welcomeReleaseDelayMs / 1000)}s after entering main` : '—'}</strong>
                </div>
              </div>
            </div>

            <div className="ffs-start-fallback-row">
              <label>
                <span>Fallback welcome audio</span>
                <select
                  value={fallback?.soundId ?? ''}
                  disabled={busy || Boolean(session)}
                  onChange={(event) => void changeFallback(event.target.value || null)}
                >
                  <option value="">No fallback audio — readiness warning</option>
                  {state.sounds.map((sound) => (
                    <option value={sound.id} key={sound.id} disabled={!sound.enabled}>
                      {sound.name}{sound.enabled ? '' : ' — disabled'}
                    </option>
                  ))}
                </select>
              </label>
              <div className={`ffs-start-fallback-state ${fallback?.configured && fallback.enabled ? 'good' : 'warning'}`}>
                {fallback?.configured && fallback.enabled
                  ? `${fallback.soundName || 'Fallback configured'} · ready`
                  : fallback?.configured
                    ? 'Configured sound is disabled'
                    : 'No fallback is configured for this theme'}
              </div>
            </div>

            {session ? <p className="ffs-workspace-note">End the active session before changing the theme or fallback welcome audio.</p> : null}
          </section>

          <div className={`ffs-session-readiness ${blockers.length ? 'warning' : 'ready'}`} role="status">
            {blockers.length ? <AlertTriangle size={17} /> : null}
            <span>{blockers.length
              ? `${blockers.length} readiness check${blockers.length === 1 ? '' : 's'} unresolved. Review live status in Control Room; starting a session will still require confirmation.`
              : 'Readiness checks passed. Live connection details are available in Control Room.'}</span>
            <a href="#/home">Control Room</a>
          </div>

          <section className="ffs-workspace-card">
            <header><div><small>2 · SESSION</small><h3>Welcome session</h3></div></header>
            {!session ? (
              <div className="ffs-primary-action">
                <div>
                  <strong>{blockers.length ? `Start with ${blockers.length} unresolved check${blockers.length === 1 ? '' : 's'}` : 'Start FFSControl stream session'}</strong>
                  <span>{blockers.length ? 'You can deliberately continue, but FFSControl will show the unresolved checks again before it starts.' : 'This starts the welcome cycle and waits for the configured OBS intro-scene gate. It does not change OBS scenes.'}</span>
                </div>
                <button type="button" className={`ffs-button ${blockers.length ? 'warning' : 'primary'}`} disabled={busy} onClick={() => void beginSession()}>{blockers.length ? 'Start Anyway' : 'Start Session'}</button>
              </div>
            ) : (
              <>
                <div className="ffs-session-state">
                  <div><small>QUEUE STATE</small><strong>{session.showStarted ? 'Live' : releaseSeconds !== null ? releaseSeconds > 0 ? `Releasing in ${releaseSeconds}s` : 'Releasing now' : session.introSceneSeen ? 'Waiting for main scene' : 'Waiting for startup scene'}</strong></div>
                  <div><small>SCENE GATE</small><strong>{session.introSceneName || 'First observed live scene'}</strong><span>Last OBS: {session.lastObservedSceneName || 'Waiting'}</span></div>
                </div>
                <div className="ffs-count-grid">
                  <div><span>Queued</span><strong>{session.welcomes.queued.toLocaleString('en-GB')}</strong></div>
                  <div><span>Playing</span><strong>{session.welcomes.playing.toLocaleString('en-GB')}</strong></div>
                  <div><span>Welcomed</span><strong>{session.welcomes.welcomed.toLocaleString('en-GB')}</strong></div>
                  <div><span>Failed</span><strong>{session.welcomes.failed.toLocaleString('en-GB')}</strong></div>
                </div>
                <div className="ffs-inline-actions">
                  <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void newCycle()}>Start New Welcome Cycle</button>
                </div>
              </>
            )}
          </section>

          {session ? (
            <section className="ffs-workspace-card danger-zone">
              <header><div><small>MANUAL / EMERGENCY</small><h3>Overrides</h3></div><ShieldAlert size={19} /></header>
              <p>These controls bypass normal stream-session behaviour. They are deliberately kept away from the routine workflow.</p>
              <div className="ffs-inline-actions">
                <button type="button" className="ffs-button warning" disabled={busy || session.showStarted} onClick={() => void releaseNow()}>Release Queue Now</button>
                <button type="button" className="ffs-button danger" disabled={busy} onClick={() => void endSession()}>End Session</button>
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
